import { AdvancedTrackDefinition } from '../../api/types'
import { roleLabelFor } from '../careTeam/roleCategories'
import { RoleCategoryId } from '../careTeam/types'
import { advancedColors, surfaces } from '../../theme'

export type FeedCategoryStyle = { bg: string; fg: string; stroke: string }

const ROLE_STYLES: Partial<Record<RoleCategoryId, FeedCategoryStyle>> = {
  therapist: surfaces.green,
  nutritionist: surfaces.blue,
  coach: surfaces.earth,
  'care-coordinator': surfaces.pink,
  psychiatrist: surfaces.yellow,
}

// Dev-seed keys that predate the catalog; a catalog track uses its own `ownerRole` and `title`.
const TRACK_STYLES: Record<string, FeedCategoryStyle> = {
  nutrition: surfaces.blue,
  keto: surfaces.blue,
  'insulin-resistance': surfaces.blue,
  sleep: surfaces.violet,
  activity: surfaces.earth,
  orientation: surfaces.green,
}

const TRACK_LABELS: Record<string, string> = {
  nutrition: 'Nutrition',
  keto: 'Keto',
  'insulin-resistance': 'Insulin resistance',
  sleep: 'Sleep',
  activity: 'Activity',
  orientation: 'Orientation',
}

const TRACKS_OWNED_BY_ROLE: Partial<Record<RoleCategoryId, readonly string[]>> = {
  nutritionist: ['nutrition', 'keto', 'insulin-resistance'],
  coach: ['activity'],
  'care-coordinator': ['sleep'],
  therapist: ['orientation'],
}

// `ProviderRoleId` and `RoleCategoryId` agree on every role but this one.
const ROLE_CATEGORY_IDS: Record<string, RoleCategoryId> = { careCoordinator: 'care-coordinator' }

export const roleCategoryIdFor = (role: string | null): RoleCategoryId | null =>
  role ? ROLE_CATEGORY_IDS[role] ?? role : null

type Tracks = readonly AdvancedTrackDefinition[]

const catalogTrack = (tracks: Tracks, category: string) => tracks.find((track) => track.trackKey === category)

export const viewerOwnsCategory = (
  category: string,
  viewerCategory: RoleCategoryId | null,
  tracks: Tracks = []
): boolean => {
  if (!viewerCategory || category === 'system' || category === 'patient') {
    return false
  }
  const track = catalogTrack(tracks, category)

  return (
    category === viewerCategory ||
    (track ? track.ownerRole === viewerCategory : (TRACKS_OWNED_BY_ROLE[viewerCategory] ?? []).includes(category))
  )
}

// `yellow` is `SYSTEM_STYLE`'s exact tone, so a hashed category could pass for "Care team & app".
const FALLBACK_STYLES = Object.values(surfaces).filter((style) => style !== surfaces.yellow)

const hashStyle = (id: string): FeedCategoryStyle => {
  const sum = Array.from(id).reduce((total, char) => total + char.charCodeAt(0), 0)

  return FALLBACK_STYLES[sum % FALLBACK_STYLES.length]
}

const SYSTEM_STYLE: FeedCategoryStyle = {
  bg: advancedColors.amberSoft,
  fg: advancedColors.amber,
  stroke: advancedColors.amberSoft,
}

export const NEUTRAL_STYLE: FeedCategoryStyle = {
  bg: advancedColors.cardBg,
  fg: advancedColors.inkMuted,
  stroke: advancedColors.line,
}

// A catalog track without its own tone takes its owner role's.
export const styleForCategory = (category: string, tracks: Tracks = []): FeedCategoryStyle => {
  if (category === 'system') {
    return SYSTEM_STYLE
  }
  if (category === 'patient') {
    return NEUTRAL_STYLE
  }
  const ownerRole = catalogTrack(tracks, category)?.ownerRole

  return (
    ROLE_STYLES[category] ??
    TRACK_STYLES[category] ??
    (ownerRole ? ROLE_STYLES[ownerRole] : undefined) ??
    hashStyle(category)
  )
}

export const OWN_TRACK_STYLE: FeedCategoryStyle = {
  bg: advancedColors.surface,
  fg: advancedColors.ink,
  stroke: advancedColors.line,
}

export const styleForFeedActivity = (
  category: string,
  viewerCategory: RoleCategoryId | null,
  tracks: Tracks = []
): FeedCategoryStyle =>
  viewerOwnsCategory(category, viewerCategory, tracks) ? OWN_TRACK_STYLE : styleForCategory(category, tracks)

export const labelForCategory = (category: string, tracks: Tracks = []): string => {
  if (category === 'system') {
    return 'Care team & app'
  }
  if (category === 'patient') {
    return 'Patient'
  }

  return catalogTrack(tracks, category)?.title ?? TRACK_LABELS[category] ?? roleLabelFor(category)
}
